import React, { useState } from 'react'
import { Trans } from '@lingui/react'
import { useApolloClient } from '@apollo/client'

import useCreateActivityForm from './useCreateActivityForm'
import CreateActivityModal from './CreateActivityModal'

import useAppState from '../../hooks/useAppState'
import useNotifications from '../../hooks/useNotifications'
import usePermissions from '../../hooks/usePermissions'
import ModalUnauthorized from '../../layout/ModalUnauthorized'
import useTracking from '../../hooks/useTracking'

import { formatTime, today } from '../../../util/dates'
import createActivity from '../../../api/createActivity'

const NOW = formatTime(Date.now())
const TODAY = today()

const CreateActivityModalWithState = ({
    end = NOW,
    start = NOW,
    date = TODAY,
    project = null,
    type = null,
    description = '',
    timerId = null,
    onClose,
    onComplete,
    ...props
}) => {
    const { canCreateActivities } = usePermissions()
    const { delegatedUser } = useAppState()
    const { trackError } = useTracking()
    const { dispatchError, dispatchSuccess } = useNotifications()
    const apolloClient = useApolloClient()
    const formState = useCreateActivityForm({
        project,
        date,
        start,
        end,
        description,
        type,
        timerId,
    })

    const [isSubmitting, setIsSubmitting] = useState(false)
    if (canCreateActivities.value !== true) {
        return <ModalUnauthorized />
    }
    return (
        <CreateActivityModal
            formState={formState}
            onSubmit={async () => {
                const isValid = formState.validate()
                if (isValid) {
                    setIsSubmitting(true)
                    try {
                        const input = {
                            ...formState.valuesToInput(),
                            partnerId: delegatedUser.id 
                        }
                        await createActivity(apolloClient, input, {
                            update: (proxy, { data }) => {
                                const { activity } = data.createActivity
                                const newActivity = {
                                    ...activity,
                                    relatedActivityIds: [],
                                }
                                const path = 'activitiesPaginated.nodes'
                                onComplete(path, newActivity)
                            },
                        })
                        dispatchSuccess({
                            content: (
                                <Trans id="You successfully created this activity!" />
                            ),
                        })
                        onClose()
                    } catch (e) {
                        trackError(e)
                        dispatchError({
                            title: <Trans id="Something went wrong!" />,
                            content: e.message,
                        })
                        setIsSubmitting(false)
                    }
                }
            }}
            onClose={onClose}
            isSubmitting={isSubmitting}
            {...props}
        />
    )
}

export default CreateActivityModalWithState
